import type { TFunction } from "i18next";
import { Fragment, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Decision } from "../../api/access/decisions";
import { requirementOf } from "../feedback/requirement";
import { classNames } from "../styles/classNames";
import DropdownPopover from "./DropdownPopover";

/** What choosing a menu item does: run an action, or open a page in its own window. */
export type MenuItemAction =
  | { readonly kind: "select"; readonly onSelect: () => void }
  | { readonly kind: "open"; readonly href: string };

/** A second action on an item's row, such as a gear for that item's settings. It is its own
 * menu item, so the keyboard and screen readers reach it; the eye sees it on hover or focus. */
export interface MenuRowAction {
  readonly id: string;
  /** The accessible name and tooltip, for example "Settings of Quellmar letters". */
  readonly label: string;
  readonly icon: ReactNode;
  readonly onSelect: () => void;
}

export interface MenuItem {
  readonly id: string;
  readonly label: string;
  readonly action: MenuItemAction;
  readonly icon?: ReactNode;
  readonly disabled?: boolean;
  /** Why it can't be chosen here and now (P-B), shown under its label: it stays in the keyboard order, says why, and
   * choosing it does nothing, as an unavailable `AppButton` does. */
  readonly unavailable?: string;
  /** The served decision choosing it needs (SOT-02, accounts.md GS-8): refused about where things are, it isn't
   * listed; otherwise it is unavailable with why, a missing permission named as BlockedState names it. */
  readonly requires?: Decision;
  readonly rowAction?: MenuRowAction;
  /** Extra classes for the entry, such as a highlight that shows where something lives. */
  readonly className?: string;
}

export interface MenuSection {
  readonly id: string;
  /** Names the group for screen readers and shows above it; none for an unnamed group. */
  readonly heading?: string;
  /** Shows the heading as a title, such as the current project's name, not as a label. */
  readonly headingIsTitle?: boolean;
  readonly items: readonly MenuItem[];
}

/** What the trigger button needs: spread it onto a real `<button>`. */
export interface MenuTriggerProps {
  readonly id: string;
  readonly "aria-haspopup": "menu";
  readonly "aria-expanded": boolean;
  readonly "aria-controls": string | undefined;
  readonly onClick: () => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
}

interface MenuButtonProps {
  sections: readonly MenuSection[];
  /** Draws the button that opens the menu; `open` lets it turn its chevron. */
  renderTrigger: (props: MenuTriggerProps, open: boolean) => ReactNode;
  align?: "left" | "right";
  width?: "sm" | "md" | "lg";
  className?: string;
  /** Shown above the items, outside the menu's keyboard order, such as whose account menu this is. */
  header?: ReactNode;
  /** Shown below the items, outside the menu's keyboard order, such as which deploy runs (F160).
   * It draws its own separator, so a footer that renders nothing leaves none, and it mounts only
   * while the menu is open. */
  footer?: ReactNode;
}

interface FocusStop {
  readonly key: string;
  readonly disabled: boolean;
}

type StopProps = (key: string) => { ref: (element: HTMLElement | null) => void; role: "menuitem"; tabIndex: number };

/**
 * A menu button (WAI-ARIA APG "Menu Button"), the one menu of the web app. Enter, Space or
 * the arrow keys open it on an item; the arrows, Home and End move between items, row
 * actions included; Escape closes it and returns to the button; Tab or a click outside
 * closes it. Choosing an item closes the menu first. No submenus: they fail on touch and
 * are fiddly with a mouse (web-app.md WA-5).
 */
export default function MenuButton({ sections: given, renderTrigger, align = "left", width = "sm", className, header, footer }: MenuButtonProps) {
  const { t } = useTranslation();
  const sections = given.map((section) => ({ ...section, items: section.items.flatMap((item) => decided(t, item)) }));
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(0);
  const buttonId = useId();
  const menuId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);
  const stopRefs = useRef(new Map<string, HTMLElement | null>());
  const stops = focusStops(sections);
  const enabled = stops.flatMap((stop, index) => (stop.disabled ? [] : [index]));
  // An item disabled while the menu is open hands focus to the first enabled one.
  const active = enabled.includes(focused) ? focused : enabled[0];
  const activeKey = active === undefined ? undefined : stops[active]?.key;

  useEffect(() => {
    if (open && activeKey !== undefined) {
      stopRefs.current.get(activeKey)?.focus();
    }
  }, [open, activeKey]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && anchorRef.current?.contains(event.target) !== true) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
    };
  }, [open]);

  if (stops.length === 0) {
    return null;
  }

  const openAt = (index: number | undefined) => {
    if (index === undefined) {
      return;
    }
    setFocused(index);
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    document.getElementById(buttonId)?.focus();
  };
  const step = (by: 1 | -1) => {
    const position = active === undefined ? -1 : enabled.indexOf(active);
    setFocused(enabled[(position + by + enabled.length) % enabled.length] ?? focused);
  };
  const choose = (run: () => void) => {
    close();
    run();
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openAt(enabled[0]);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      openAt(enabled.at(-1));
    }
  };
  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, () => void> = {
      ArrowDown: () => { step(1); },
      ArrowUp: () => { step(-1); },
      Home: () => { setFocused(enabled[0] ?? focused); },
      End: () => { setFocused(enabled.at(-1) ?? focused); },
      Escape: close,
    };
    const move = moves[event.key];
    if (move !== undefined) {
      event.preventDefault();
      move();
    } else if (event.key === " " && event.target instanceof HTMLAnchorElement) {
      // A link item answers Space as the button items do.
      event.preventDefault();
      event.target.click();
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  };

  const stopProps: StopProps = (key) => ({
    ref: (element) => { stopRefs.current.set(key, element); },
    role: "menuitem",
    tabIndex: key === activeKey ? 0 : -1,
  });

  return (
    <div ref={anchorRef} className={classNames("app-dropdown-anchor", className)}>
      {renderTrigger(
        {
          id: buttonId,
          "aria-haspopup": "menu",
          "aria-expanded": open,
          "aria-controls": open ? menuId : undefined,
          onClick: () => { if (open) { setOpen(false); } else { openAt(enabled[0]); } },
          onKeyDown: onTriggerKeyDown,
        },
        open,
      )}
      <DropdownPopover open={open} align={align} width={width}>
        {header === undefined ? null : <>{header}<div aria-hidden="true" className="app-menu-separator" /></>}
        <div id={menuId} role="menu" aria-labelledby={buttonId} className="p-1" onKeyDown={onMenuKeyDown}>
          {sections.filter((section) => section.items.length > 0).map((section, sectionIndex) => (
            <Fragment key={section.id}>
              {sectionIndex > 0 ? <div role="separator" className="app-menu-separator my-1" /> : null}
              <div role="group" aria-label={section.heading}>
                {section.heading === undefined ? null : (
                  <p aria-hidden="true" className={section.headingIsTitle === true ? "app-title truncate px-3 pb-0.5 pt-1.5 text-sm font-semibold" : "app-label px-3 pb-0.5 pt-1.5 text-xs"}>
                    {section.heading}
                  </p>
                )}
                {section.items.map((item) => (
                  <MenuRow key={item.id} item={item} stopProps={stopProps} choose={choose} close={close} />
                ))}
              </div>
            </Fragment>
          ))}
        </div>
        {footer}
      </DropdownPopover>
    </div>
  );
}

const ITEM_CLASS = "app-nav-item flex min-w-0 flex-1 items-center gap-2 px-3 py-1.5 text-left text-sm disabled:cursor-not-allowed disabled:opacity-40 aria-disabled:cursor-not-allowed";

interface MenuRowProps {
  item: MenuItem;
  stopProps: StopProps;
  choose: (run: () => void) => void;
  close: () => void;
}

function MenuRow({ item, stopProps, choose, close }: MenuRowProps) {
  const { action, rowAction } = item;
  const content = (
    <>
      {item.icon === undefined ? null : <span className="shrink-0" aria-hidden="true">{item.icon}</span>}
      {item.unavailable === undefined ? (
        <span className="truncate">{item.label}</span>
      ) : (
        <span className="flex min-w-0 flex-col">
          <span className="app-text-muted truncate">{item.label}</span>
          <span className="app-text-muted text-xs">{item.unavailable}</span>
        </span>
      )}
    </>
  );
  // An unavailable link item is no link, as a refused `ButtonLink` is none: it shows as an unavailable button does.
  return (
    <div role="none" className="group flex items-center">
      {action.kind === "open" && item.unavailable === undefined ? (
        <a {...stopProps(item.id)} href={action.href} target="_blank" rel="noopener" className={classNames(ITEM_CLASS, item.className)} onClick={() => { close(); }}>
          {content}
        </a>
      ) : (
        <button
          {...stopProps(item.id)}
          type="button"
          disabled={item.unavailable === undefined ? item.disabled : undefined}
          aria-disabled={item.unavailable === undefined ? undefined : "true"}
          className={classNames(ITEM_CLASS, item.className)}
          onClick={item.unavailable !== undefined || action.kind === "open" ? undefined : () => { choose(action.onSelect); }}
        >
          {content}
        </button>
      )}
      {rowAction === undefined ? null : (
        <button
          {...stopProps(rowAction.id)}
          type="button"
          aria-label={rowAction.label}
          title={rowAction.label}
          className="app-nav-item mr-1 shrink-0 p-1.5 opacity-0 focus:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
          onClick={() => { choose(rowAction.onSelect); }}
        >
          {rowAction.icon}
        </button>
      )}
    </div>
  );
}

/** The item as its decision has it (`requirementOf`, the rule every control follows): none, refused for where things
 * are, or unavailable with why. */
function decided(t: TFunction, item: MenuItem): MenuItem[] {
  if (item.requires === undefined) return [item];
  const requirement = requirementOf(t, item.requires);
  if (!requirement.shown) return [];
  return requirement.reason === null ? [item] : [{ ...item, unavailable: requirement.reason }];
}

/** Every item and row action in reading order: the arrow keys walk this list. */
function focusStops(sections: readonly MenuSection[]): FocusStop[] {
  return sections.flatMap((section) =>
    section.items.flatMap((item) => {
      const own: FocusStop = { key: item.id, disabled: item.disabled === true && item.unavailable === undefined };
      return item.rowAction === undefined ? [own] : [own, { key: item.rowAction.id, disabled: false }];
    }),
  );
}
